import {
  VStack, Text, Box, Spinner,
  Icon, IconButton, HStack,
} from '@chakra-ui/react';
import { CirclePlus, Star } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import SearchInput from '@app/components/chakra/search-input';
import { Tooltip } from '@app/components/chakra/snippets/tooltip';
import UserRow from '@app/components/chakra/user-row';
import { useInfiniteScroll } from '@app/hooks/use-infinite-scroll';
import { useUsersQuery } from '@app/services/user';
import { type User } from '@app/types/api/user';

const AddUsers = () => {
  const [searchText, setSearchText] = useState('');
  const {
    data: usersData,
    isLoading: isUsersLoading,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
  } = useUsersQuery({ searchText });

  const lastPostElementRef = useInfiniteScroll(hasNextUsersPage, fetchNextUsersPage);

  const {
    setValue,
    watch,
    trigger,
  } = useFormContext();

  const handleAddUser = useCallback((user: User, type: 'members' | 'leads') => {
    setValue(type, [...watch(type), user]);
    trigger(type);
  }, [setValue, watch, trigger]);

  const members = watch('members');
  const leads = watch('leads');

  const users = usersData?.pages.flatMap((page) => page.users);

  return (
    <Box h="100%" display="flex" flexDirection="column">
      <Box p={2} bg="white" flexShrink={0}>
        <SearchInput
          placeholder="Search users"
          searchText={searchText}
          onChange={(value) => setSearchText(value)}
        />
      </Box>
      <Box flex="1" overflow="auto" minH="0">
        {isUsersLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" h="100%" data-testid="loading-spinner">
            <Spinner size="lg" />
          </Box>
        )}
        {!isUsersLoading && users && (
          <VStack
            align="flex-start"
            py={2}
            css={{
              '& .user-row-box:hover': {
                backgroundColor: 'gray.100',
              },
              '& .user-row-box': {
                padding: '8px',
              },
            }}
          >
            {users?.filter(
              (user: User) => !members.some(
                (member: User) => member.id === user.id,
              ) && !leads.some(
                (lead: User) => lead.id === user.id,
              ),
            ).map((user: User) => (
              <UserRow
                key={user.id}
                option={{
                  ...user,
                  ...user.profile,
                }}
                size="sm"
                showTitle
                rightElement={(
                  <HStack>
                    <Tooltip showArrow content="Add group lead">
                      <IconButton
                        aria-label="Add group lead"
                        name="Add group lead"
                        variant="ghost"
                        _hover={{
                          bg: 'none',
                        }}
                        onClick={() => handleAddUser(user, 'leads')}
                      >
                        <Icon><Star fill="currentColor" /></Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip showArrow content="Add member">
                      <IconButton
                        aria-label="Add member"
                        name="Add member"
                        variant="ghost"
                        _hover={{
                          bg: 'none',
                        }}
                        onClick={() => handleAddUser(user, 'members')}
                      >
                        <Icon><CirclePlus /></Icon>
                      </IconButton>
                    </Tooltip>
                  </HStack>
                )}
              />
            ))}
            {hasNextUsersPage && (
              <div ref={lastPostElementRef} />
            )}
          </VStack>
        )}
        {!isUsersLoading && !users?.length && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Text>No users found</Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AddUsers;
